import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShieldCheck, LogOut, LayoutDashboard, MessageSquare, ClipboardList, Users, CalendarDays, Building2, Home, CreditCard, FileSearch, BadgeCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useConfig } from "../context/ConfigContext";

const ROLE_LABELS = { family: "Family Portal", agency: "Agency Portal", admin: "Admin Console", subcontractor: "Subcontractor Portal" };

// Each link declares whether it requires the PHI feature flag. Links flagged
// `phiOnly: true` are hidden in Workforce-Compliance V1 (PHI_FEATURES_ENABLED=false).
const NAV_LINKS = [
  { to: "/dashboard",     label: "Dashboard",      icon: LayoutDashboard, testId: "nav-link-dashboard",      roles: ["family", "agency", "admin", "subcontractor"] },
  { to: "/credentials",   label: "Credentials",    icon: BadgeCheck,      testId: "nav-link-credentials",    roles: ["agency", "admin"] },
  { to: "/caregivers",    label: "Employees",      icon: Users,           testId: "nav-link-caregivers",     roles: ["agency", "admin", "subcontractor"] },
  { to: "/audit-packet",  label: "Audit Prep",     icon: FileSearch,      testId: "nav-link-audit-packet",   roles: ["agency", "admin"] },
  // ─── PHI / parked-in-V1 routes ────────────────────────────────────────────
  { to: "/schedule",      label: "Schedule",       icon: CalendarDays,    testId: "nav-link-schedule",       roles: ["family", "agency", "admin", "subcontractor"], phiOnly: true },
  { to: "/property",      label: "Property",       icon: Home,            testId: "nav-link-property",       roles: ["family", "agency", "admin", "subcontractor"], phiOnly: true },
  { to: "/messages",      label: "Messages",       icon: MessageSquare,   testId: "nav-link-messages",       roles: ["family", "agency", "admin"],                  phiOnly: true },
  { to: "/careplan",      label: "Care Plan",      icon: ClipboardList,   testId: "nav-link-careplan",       roles: ["family", "agency", "admin"],                  phiOnly: true },
  { to: "/subcontractors", label: "Subcontractors", icon: Building2,      testId: "nav-link-subcontractors", roles: ["agency", "admin"],                            phiOnly: true },
  { to: "/billing",       label: "Billing",        icon: CreditCard,      testId: "nav-link-billing",        roles: ["family", "agency", "subcontractor"] },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { phiEnabled } = useConfig();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#F9F7F4]">
      <nav className="sticky top-0 z-50 bg-[#F9F7F4]/80 backdrop-blur-xl border-b border-[#E8E6E1]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" data-testid="nav-brand" className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-[#2E5C50] rounded-md flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-heading font-bold text-lg tracking-tight text-[#1C211F]">Nexari Health</span>
            {user && (
              <span className="hidden sm:inline-block ml-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#57605A] border-l border-[#E8E6E1] pl-3">
                {ROLE_LABELS[user.role]}
              </span>
            )}
          </Link>
          <div className="hidden md:flex items-center gap-1">
            {NAV_LINKS
              .filter((l) => user && l.roles.includes(user.role))
              .filter((l) => phiEnabled || !l.phiOnly)
              .map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                  pathname === l.to ? "bg-[#D6E0D9]/50 text-[#2E5C50]" : "text-[#57605A] hover:text-[#2E5C50]"
                }`}
              >
                <l.icon className="w-4 h-4" strokeWidth={1.5} />
                {l.label}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-4">
            {user && (
              <span data-testid="nav-user-name" className="text-sm text-[#57605A] hidden sm:block">
                {user.name}
              </span>
            )}
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-sm font-medium text-[#57605A] hover:text-[#B54D40] transition-colors duration-200"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
              Sign out
            </button>
          </div>
        </div>
      </nav>
      <main className="max-w-7xl mx-auto px-6 py-10">{children}</main>
    </div>
  );
};
